import React, { useState } from "react";
import { Company, CompanyUpdatePayload } from "../../types/company.types";
import { formatCnpj, formatPhoneBR } from "../../lib/formatters";
import { Button, Dialog, Field, Input } from "../ui";
import { t } from "../../i18n";

type EditableField = "phone" | "email" | "cnpj";

const FIELD_LABELS: Record<EditableField, string> = {
  phone: t("links.company.phone"),
  email: t("links.company.email"),
  cnpj:  t("links.company.cnpj"),
};

const EDITABLE_FIELDS: EditableField[] = ["phone", "email", "cnpj"];

// Dado já cadastrado: só exibido, sem edição.
const ReadOnlyField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-caption font-medium text-fg-muted">{label}</span>
    <p className="break-words rounded-control bg-surface-muted px-3 py-2 text-body text-fg">{value}</p>
  </div>
);

interface CompanyEditModalProps {
  company: Company;
  saving: boolean;
  onConfirm: (payload: CompanyUpdatePayload) => void;
  onClose: () => void;
  // Quando true, o e-mail deixa de ser um campo opcional que pode ser pulado
  // via "Apenas vincular" — usado no fluxo de envio por email, onde a empresa
  // só pode ser selecionada como destinatária se tiver um e-mail cadastrado.
  requireEmail?: boolean;
}

const CompanyEditModal: React.FC<CompanyEditModalProps> = ({ company, saving, onConfirm, onClose, requireEmail = false }) => {
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [cnpj, setCnpj] = useState("");

  // Só os campos que a empresa ainda não tem viram inputs editáveis —
  // dados já preenchidos são só exibidos (podem pertencer a outro médico
  // que também vinculou esta mesma empresa).
  const missingFields = EDITABLE_FIELDS.filter((field) => !company[field]);
  const hasMissingFields = missingFields.length > 0;
  const emailRequiredAndMissing = requireEmail && !company.email;

  const fieldValue: Record<EditableField, string> = { phone, email, cnpj };
  const setFieldValue: Record<EditableField, (v: string) => void> = {
    phone: (v) => setPhone((prev) => formatPhoneBR(v, prev)),
    email: (v) => setEmail(v.toLowerCase()),
    cnpj:  (v) => setCnpj(formatCnpj(v)),
  };

  const buildPayload = (): CompanyUpdatePayload => {
    const payload: CompanyUpdatePayload = {};
    if (missingFields.includes("phone") && phone.trim()) payload.phone = phone.trim();
    if (missingFields.includes("email") && email.trim()) payload.email = email.trim();
    if (missingFields.includes("cnpj")  && cnpj.trim())  payload.cnpj  = cnpj.trim();
    return payload;
  };

  // "Salvar e vincular" só faz sentido se pelo menos um campo faltante foi
  // preenchido — sem isso, seria idêntico a "Apenas vincular", só que
  // chamando um PUT à toa.
  const hasChanges = missingFields.some((field) => fieldValue[field].trim());
  // Com e-mail obrigatório, o botão só libera quando o campo foi preenchido —
  // não basta "alguma mudança", precisa ser especificamente o e-mail.
  const emailSatisfied = !emailRequiredAndMissing || email.trim().length > 0;
  // Empresa não pode ser vinculada sem completar o e-mail nesse fluxo, então
  // a saída "pular e vincular assim mesmo" não é oferecida.
  const canSkip = hasMissingFields && !emailRequiredAndMissing;

  const description = emailRequiredAndMissing
    ? t("links.company.reviewEmailMissing")
    : hasMissingFields
    ? t("links.company.reviewMissing")
    : t("links.company.reviewComplete");

  return (
    <Dialog
      open
      size="sm"
      onClose={onClose}
      closeDisabled={saving}
      title={t("links.company.reviewTitle")}
      description={description}
      footer={
        <>
          {canSkip && (
            <Button variant="outline" onClick={() => onConfirm({})} disabled={saving}>
              {t("links.linkOnly")}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => onConfirm(buildPayload())}
            disabled={saving || (hasMissingFields && !hasChanges) || !emailSatisfied}
          >
            {saving ? t("links.saving") : hasMissingFields ? t("links.saveAndLink") : t("links.company.linkComplete")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <ReadOnlyField label={t("links.name")} value={company.name} />

        {EDITABLE_FIELDS.map((field) => {
          const currentValue = company[field];
          const label = FIELD_LABELS[field];
          if (currentValue) return <ReadOnlyField key={field} label={label} value={currentValue} />;

          const isRequired = field === "email" && requireEmail;
          return (
            <Field
              key={field}
              label={
                <>
                  {label}{" "}
                  <span className="font-normal text-fg-subtle">
                    {isRequired ? t("links.company.fieldRequired") : t("links.company.fieldMissing")}
                  </span>
                </>
              }
            >
              <Input
                type={field === "email" ? "email" : "text"}
                value={fieldValue[field]}
                disabled={saving}
                onChange={(e) => setFieldValue[field](e.target.value)}
                placeholder={t("links.company.fieldPlaceholder", { field: label.toLowerCase() })}
              />
            </Field>
          );
        })}
      </div>
    </Dialog>
  );
};

export default CompanyEditModal;
